import React from "react";
import {
  ExclamationCircleOutlined,
  ReloadOutlined,
  HomeOutlined,
  ToolOutlined,
  GlobalOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  LockOutlined,
  LaptopOutlined,
  InfoCircleOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { blue } from "@ant-design/colors";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorType: null,
      errorMessage: null,
      errorIcon: null,
      suggestions: [],
    };
  }

  static getDerivedStateFromError(error) {
    // Actualizar el estado para que el siguiente renderizado muestre la UI de fallback
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // You can also log the error to an error reporting service

    // Determinar el tipo de error
    let errorType = "Oops, algo salió mal";
    let errorMessage =
      "Lo sentimos, ha ocurrido un error inesperado. Por favor, intenta recargar la página.";
    let errorIcon = ExclamationCircleOutlined;
    let suggestions = [];

    const errorMsg = error?.message || "";

    // Errores de compilación/build
    if (
      errorMsg.includes("Module build failed") ||
      errorMsg.includes("ENOENT") ||
      errorMsg.includes("Cannot find module")
    ) {
      errorType = "Error de Compilación";
      errorMessage =
        "Hay un problema con las dependencias de la aplicación. Esto suele resolverse recargando la página o reiniciando el servidor de desarrollo.";
      errorIcon = ToolOutlined;
      suggestions = [
        "Recarga la página completamente (Ctrl+Shift+R o Cmd+Shift+R)",
        "Si el problema persiste, contacta al equipo de desarrollo",
      ];
    }
    // Errores de red
    else if (
      errorMsg.includes("Network Error") ||
      errorMsg.includes("Failed to fetch")
    ) {
      errorType = "Error de Conexión";
      errorMessage =
        "No se pudo conectar con el servidor. Verifica tu conexión a internet.";
      errorIcon = GlobalOutlined;
      suggestions = [
        "Verifica tu conexión a internet",
        "Asegúrate de que el servidor esté funcionando",
        "Intenta nuevamente en unos momentos",
      ];
    }
    // Errores de timeout
    else if (errorMsg.includes("timeout") || errorMsg.includes("timed out")) {
      errorType = "Tiempo de Espera Agotado";
      errorMessage =
        "La petición tardó demasiado en responder. El servidor puede estar ocupado.";
      errorIcon = ClockCircleOutlined;
      suggestions = [
        "Espera unos momentos e intenta nuevamente",
        "Verifica tu conexión a internet",
      ];
    }
    // Errores del servidor
    else if (error.response?.status === 500) {
      errorType = "Error del Servidor";
      errorMessage =
        "El servidor encontró un problema al procesar tu solicitud.";
      errorIcon = CloseCircleOutlined;
      suggestions = [
        "Intenta nuevamente en unos momentos",
        "Si el problema persiste, contacta al administrador",
      ];
    }
    // Recurso no encontrado
    else if (error.response?.status === 404) {
      errorType = "Página No Encontrada";
      errorMessage =
        "La página o recurso que buscas no existe o ha sido movido.";
      errorIcon = SearchOutlined;
      suggestions = [
        "Verifica que la URL sea correcta",
        "Regresa a la página anterior",
      ];
    }
    // Acceso denegado
    else if (error.response?.status === 403) {
      errorType = "Acceso Denegado";
      errorMessage = "No tienes permisos para acceder a este recurso.";
      errorIcon = LockOutlined;
      suggestions = [
        "Verifica que tengas los permisos necesarios",
        "Contacta al administrador si crees que esto es un error",
      ];
    }
    // No autorizado
    else if (error.response?.status === 401) {
      errorType = "Sesión Expirada";
      errorMessage =
        "Tu sesión ha expirado. Por favor, inicia sesión nuevamente.";
      errorIcon = LockOutlined;
      suggestions = [
        "Inicia sesión nuevamente",
        "Asegúrate de que tu sesión no haya expirado",
      ];
    }
    // Errores de JavaScript
    else if (
      errorMsg.includes("Cannot read property") ||
      errorMsg.includes("is not defined")
    ) {
      errorType = "Error de Aplicación";
      errorMessage =
        "Hay un problema con la aplicación. Esto puede ser temporal.";
      errorIcon = LaptopOutlined;
      suggestions = [
        "Recarga la página completamente",
        "Limpia el cache del navegador si el problema persiste",
      ];
    }

    this.setState({
      error: error,
      errorInfo: errorInfo,
      errorType: errorType,
      errorMessage: errorMessage,
      errorIcon: errorIcon,
      suggestions: suggestions,
    });
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            padding: "20px",
            textAlign: "center",
            backgroundColor: "#F0F2F2",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "40px",
              borderRadius: "12px",
              boxShadow: `0 4px 16px ${blue[1]}`,
              maxWidth: "600px",
              width: "100%",
            }}
          >
            <div style={{ marginBottom: "24px", textAlign: "center" }}>
              <div 
                style={{ 
                  fontSize: "64px", 
                  marginBottom: "16px",
                  color: blue[6],
                  display: "flex",
                  justifyContent: "center",
                  filter: "drop-shadow(0 2px 4px rgba(24, 144, 255, 0.15))"
                }}
              >
                {React.createElement(this.state.errorIcon || ExclamationCircleOutlined, {
                  style: { fontSize: "64px" }
                })}
              </div>
              <h1
                style={{
                  color: blue[7],
                  marginBottom: "12px",
                  fontSize: "24px",
                  fontWeight: "600",
                }}
              >
                {this.state.errorType || "Error"}
              </h1>
              <p
                style={{
                  color: "#666",
                  marginBottom: "24px",
                  fontSize: "16px",
                  lineHeight: "1.6",
                }}
              >
                {this.state.errorMessage}
              </p>
            </div>

            {this.state.suggestions && this.state.suggestions.length > 0 && (
              <div
                style={{
                  backgroundColor: blue[0],
                  padding: "16px",
                  borderRadius: "8px",
                  marginBottom: "24px",
                  borderLeft: `4px solid ${blue[7]}`,
                }}
              >
                <strong
                  style={{
                    color: blue[7],
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                    fontSize: "15px",
                  }}
                >
                  <InfoCircleOutlined /> Sugerencias:
                </strong>
                <ul style={{ margin: 0, paddingLeft: "20px", color: "#666" }}>
                  {this.state.suggestions.map((suggestion, index) => (
                    <li key={index} style={{ marginBottom: "4px" }}>
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: "12px 24px",
                  backgroundColor: blue[7],
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: "500",
                  transition: "background-color 0.2s",
                  boxShadow: `0 2px 4px ${blue[2]}`,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onMouseOver={(e) =>
                  (e.target.style.backgroundColor = blue[6])
                }
                onMouseOut={(e) => (e.target.style.backgroundColor = blue[7])}
              >
                <ReloadOutlined /> Recargar Página
              </button>
              <button
                onClick={() => (window.location.href = "/")}
                style={{
                  padding: "12px 24px",
                  backgroundColor: blue[6],
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: "500",
                  transition: "background-color 0.2s",
                  boxShadow: `0 2px 4px ${blue[2]}`,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onMouseOver={(e) =>
                  (e.target.style.backgroundColor = blue[5])
                }
                onMouseOut={(e) => (e.target.style.backgroundColor = blue[6])}
              >
                <HomeOutlined /> Ir al Inicio
              </button>
            </div>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <details style={{ marginTop: "24px", textAlign: "left" }}>
                <summary
                  style={{
                    cursor: "pointer",
                    color: blue[7],
                    padding: "8px",
                    backgroundColor: "#F0F2F2",
                    borderRadius: "4px",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <EyeOutlined /> Detalles Técnicos (Solo Desarrollo)
                </summary>
                <div
                  style={{
                    background: "#1e1e1e",
                    color: "#d4d4d4",
                    padding: "16px",
                    borderRadius: "6px",
                    overflow: "auto",
                    maxWidth: "100%",
                    fontSize: "12px",
                    marginTop: "8px",
                    fontFamily: "monospace",
                    maxHeight: "400px",
                  }}
                >
                  <div style={{ marginBottom: "12px" }}>
                    <strong style={{ color: "#4ec9b0" }}>Error:</strong>
                    <div style={{ marginTop: "4px", color: "#f48771" }}>
                      {this.state.error && this.state.error.toString()}
                    </div>
                  </div>
                  {this.state.errorInfo && (
                    <div>
                      <strong style={{ color: "#4ec9b0" }}>Stack Trace:</strong>
                      <div
                        style={{
                          marginTop: "4px",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {this.state.errorInfo.componentStack}
                      </div>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
